// composables
import { reactive, ref, nextTick, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
// composables
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Composable for managing agreement mileage updates
 * Handles mileage modal state, validation, and submission
 */
export default function useAgreementMileage(agreementID: Ref<string | number>, agreementData: Ref<Agreement | null>) {
  // state
  const updateMileageModalIsOpen = ref(false)
  const updateMileageState = reactive({
    mileage: null as string | null
  })

  // validation rules
  const updateMileageRules = {
    mileage: {
      required: helpers.withMessage('Mileage is required', value => {
        return value != null && value !== '' && value !== undefined
      }),
      minValue: helpers.withMessage('Mileage must be greater than or equal to 0', value => {
        if (value == null || value === '') return true
        const numValue = Number(value)
        return !isNaN(numValue) && numValue >= 0
      })
    }
  }

  const updateMileageV$ = useVuelidate(updateMileageRules, updateMileageState)

  // api requests
  const updateMileageApiRequest = useApiRequest()

  /**
   * Opens the update mileage modal and initializes the form with current mileage
   */
  function openUpdateMileageModal(): void {
    const currentMileage = agreementData.value?.odometer?.current
    updateMileageState.mileage = currentMileage != null ? String(currentMileage) : null
    updateMileageModalIsOpen.value = true
    // Reset validation state when opening
    nextTick(() => {
      if (updateMileageV$.value) {
        updateMileageV$.value.$reset()
      }
    })
  }

  /**
   * Handles submitting the mileage update modal
   */
  async function submitUpdateMileage(): Promise<void> {
    try {
      // Manual validation since Vuelidate seems to have conflicts
      if (!updateMileageState.mileage || updateMileageState.mileage === '') {
        alertToast('Validation failed', 'Mileage is required', 'error')
        return
      }

      const mileageValue = Number(updateMileageState.mileage)

      if (isNaN(mileageValue)) {
        alertToast('Invalid mileage', 'Please enter a valid number', 'error')
        return
      }

      if (mileageValue < 0) {
        alertToast('Invalid mileage', 'Mileage must be greater than or equal to 0', 'error')
        return
      }

      const response = await updateMileageApiRequest.send({
        endpoint: '/v1/agreements/' + agreementID.value,
        method: 'PATCH',
        data: JSON.stringify({
          odometerCurrent: mileageValue
        })
      })

      if (!response.success) {
        alertToast('Failed to update mileage', response.message, 'error')
        return
      }

      // Update the local agreement data
      if (agreementData.value?.odometer) {
        agreementData.value.odometer.current = mileageValue
      }

      // Reset form and close modal
      updateMileageState.mileage = null
      updateMileageModalIsOpen.value = false
      if (updateMileageV$.value) {
        updateMileageV$.value.$reset()
      }

      alertToast('Mileage updated', null, 'success')
    } catch (error) {
      console.error('Error in submitUpdateMileage:', error)
      alertToast('Failed to update mileage', (error as Error).message, 'error')
    }
  }

  return {
    // state
    updateMileageModalIsOpen,
    updateMileageState,
    updateMileageV$,
    updateMileageApiRequest,
    // methods
    openUpdateMileageModal,
    submitUpdateMileage
  }
}
